<template>
    <div>
        <v-dialog
            @keydown.27="close"
            :value="value"
            @input="value => $emit('input', value)"
            scrollables
            :width="loading.data ? 350 : 1250">
            <v-sheet v-if="loading.data" class="pa-4">
                <v-progress-linear indeterminate color="primary" striped></v-progress-linear>
                <div class="text-center">Please wait ...</div>
            </v-sheet>
            <v-card flat v-else>
                <v-toolbar flat>
                    <v-toolbar-title>
                        Journal Voucher #{{ journalEntry.code }}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn v-if="journalEntry.meta" @click="openJournalEntryPrintDialog" icon>
                        <v-icon>mdi-printer</v-icon>
                    </v-btn>
                    <v-menu
                        v-if="journalEntry.meta"
                        offset-y>
                    <template v-slot:activator="{on}">
                        <v-btn
                            icon
                            v-on="on"
                            class="mx-1">
                            <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    <v-list v-if="journalEntry.meta" width="250" dense>
                        <v-list-item
                            :disabled="!journalEntry.meta.editable"
                            @click="openJournalEntryDialog({ editMode: true, id: journalEntry.id })"
                            link>
                            <v-list-item-icon>
                                <v-icon :disabled="!journalEntry.meta.editable">mdi-pencil</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('edit_journal_entry') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            :disabled="!journalEntry.meta.deletable"
                            @click="openDeleteSingleDialog({target: 'journalEntry', id: journalEntry.id})"
                            link>
                            <v-list-item-icon>
                                <v-icon :disabled="!journalEntry.meta.deletable">mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('delete_journal_entry') }}</v-list-item-title>
                        </v-list-item>
                        <v-divider v-if="!journalEntry.is_posted"></v-divider>
                        <v-list-item
                            @click="openDecisionDialog({content: $t('post_msg'), target: 'post', id: journalEntry.id })"
                            v-if="!journalEntry.is_posted"
                            link
                            :disabled="!this.dpk">
                            <v-list-item-icon v-if="!!this.dpk">
                                <v-icon>mdi-checkbox-marked-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{  this.loading.dpk ? "Please Wait ..." : $t('post_to_ledges')  }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            @click="openDecisionDialog({content: $t('reverse_msg'), target: 'reverse', id: journalEntry.id })"
                            v-if="journalEntry.status == `POSTED`"
                            link>
                            <v-list-item-icon>
                                <v-icon>mdi-autorenew</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{  $t('reverse')  }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                </v-toolbar>
                <v-card-text class="mt-4">
                    <v-row>
                        <v-col>
                            <v-simple-table dense>
                                <tbody>
                                    <tr>
                                        <th>{{ $t('date') }}</th>
                                        <td>{{ journalEntry.journal_date }}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t('document_date') }}</th>
                                        <td>{{ journalEntry.document_date }}</td>
                                    </tr>
                                    <tr v-if="journalEntry.is_posted">
                                        <th>{{ $t('posted_at') }}</th>
                                        <td>{{ journalEntry.posted_at }}</td>
                                    </tr>
                                    <tr v-if="journalEntry.is_reversed">
                                        <th>{{ $t('reverse_at') }}</th>
                                        <td>{{journalEntry.reversed_at}}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-col>
                        <v-col>
                            <v-simple-table dense>
                                <tbody>
                                    <tr>
                                        <th>{{ $t('status') }}</th>
                                        <td><p-status :state="journalEntry.status"></p-status></td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t('code') }}</th>
                                        <td>{{ journalEntry.code }}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t('reference') }}</th>
                                        <td>{{journalEntry.reference}}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t('currency') }}</th>
                                        <td>{{journalEntry.currency}}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-col>
                    </v-row>
                    <br>
                    <v-data-table
                        disable-pagination
                        hide-default-footer
                        dense
                        v-model="selected.JournalEntry"
                        :headers="journalEntryHeaders"
                        :items="journalEntry.lines">

                        <template v-slot:[`item.base_debit`]="{ value, item }">
                            <v-tooltip bottom>
                                
                                <template v-slot:activator="{on, attrs}">
                                    <span v-on="on" v-bind="attrs">
                                        {{ value > 0 ? formatNumber(value, round) : null }}
                                    </span>
                                </template>

                                <span>{{formatMoney(Math.abs(item.amount), item.currency, round)}} @ {{ formatNumber(item.rate, round)}}</span>
                            </v-tooltip>
                        </template>

                        <template v-slot:[`item.base_credit`]="{ value, item }">
                            <v-tooltip bottom>
                                
                                <template v-slot:activator="{on, attrs}">
                                    <span v-on="on" v-bind="attrs">
                                        {{ value > 0 ? formatNumber(value, round) : null }}
                                    </span>
                                </template>

                                <span>{{formatMoney(Math.abs(item.amount), item.currency, round)}} @ {{ formatNumber(item.rate, round)}}</span>
                            </v-tooltip>
                        </template>


                        <template v-slot:[`item.trans_debit`]="{ value }">
                            {{ value > 0 ? formatNumber(value, round) : null }}
                        </template>

                        <template v-slot:[`item.trans_credit`]="{ value }">
                            {{ value > 0 ? formatNumber(value, round) : null }}
                        </template>

                        <template v-slot:[`item.rate`]="{ item }">
                            <span v-if="item.rate != 1">{{item.rateCalculation}}</span>
                        </template>


                        <template v-slot:[`item.narration`]="{ value }">
                            {{ value }}
                            <!-- <p-table-width :row="item.narration"></p-table-width> -->
                        </template>


                        <template v-slot:[`body.append`]>
                            <tr class="font-weight-bold">
                                <td></td>
                                <td></td>
                                <td>{{formatNumber(totalBaseDebit, round)}}</td>
                                <td>{{formatNumber(totalBaseCredit, round)}}</td>
                                <td v-if="isForeign">{{formatNumber(totalTransDebit, round)}}</td>
                                <td v-if="isForeign">{{formatNumber(totalTransCredit, round)}}</td>
                                <td></td>
                                <td v-if="isForeign"></td>

                            </tr>
                        </template>
                    </v-data-table>
                    <br>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{ $t('narration') }}</th>
                                <td v-text="journalEntry.narration"></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <br>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th style="width: 95px;">{{ $t('created_by') }}</th>
                                <td v-text="journalEntry.created_by"></td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                            <tr v-if="journalEntry.is_posted">
                                <th style="width: 95px;">{{ $t('posted_by') }}</th>
                                <td v-text="journalEntry.posted_by"></td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                            <tr v-if="journalEntry.is_reversed">
                                <th style="width: 95px;">{{ $t('reverse_by') }}</th>
                                <td v-text="journalEntry.reversed_by"></td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
                <v-card-actions>
                    <v-btn @click="close" text>{{ $t('close_btn') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <DecisionDialog
            @submit="onDecisionDialogSubmit"
            @close="closeDecisionDialog"
            ref="decisionDialog"/>
        <journal-entry-dialog
            v-if="journalEntryDialogProps.display"
            v-model="journalEntryDialogProps.display"
            :id="journalEntryDialogProps.id"
            @close="closeJournalEntryDialog"
            @submit="createdDataFetch">
        </journal-entry-dialog>
        <delete-single-dialog
            @submit="onDeleteDialogSubmit"
            ref="deleteSingleDialog">
        </delete-single-dialog>
        <alert-dialog ref="alertDialog">
        </alert-dialog>
        <retry-dialog
            ref="retryDialog"
            @submit="onRetryDialogSubmit">
        </retry-dialog>
        <journal-entry-print-dialog
            v-if="journalEntryPrintDialogProps.display"
            :journalEntry="journalEntryPrintDialogProps.journalEntry"
            @close="closeJournalEntryPrintDialog">
        </journal-entry-print-dialog>
    </div>
</template>
<script>
// 3rd Party
import { formatMoney, formatNumber } from 'accounting';
import { mapActions } from 'vuex';
import { map } from 'lodash';

// Components
import AlertDialog from '../common/AlertDialog.vue';
import DecisionDialog from '@/components/common/DecisionDialog';
import DeleteSingleDialog from '../common/DeleteSingleDialog.vue';
import JournalEntryDialog from './JournalEntryDialog.vue';
import JournalEntryPrintDialog from './JournalEntryPrintDialog.vue';
import PStatus from '../common/PStatus.vue';
// import PTableWidth from '../common/PTableWidth.vue';
import RetryDialog from '../common/RetryDialog.vue';

// Services
import { getJournalVoucherById, postJournalVoucher, reverseJournalVoucher } from '../../services/journal/journalVoucher';
import { getDpk } from './../../services/journal';
import { getCurrenciesList } from '../../services/ledger/currency';
import { searchExchangeRates } from '../../services/ledger/exchangeRates';

// Utils
import getColor from '@/utils/getColor'
import isArabic from '@/utils/isArabic';
import truncate from '@/utils/truncate';
import { getActiveAccountsIn } from '../../services/ledger/account';
import { extractCustomRateFromJournalData, populateExchangeRateWithCustomRate, getTotal } from '../../utils/accounting';

export default {
    name: 'JournalEntryDisplayDialog',
    components: {
        DecisionDialog,
        JournalEntryDialog,
        DeleteSingleDialog,
        AlertDialog,
        RetryDialog,
        JournalEntryPrintDialog,
        // PTableWidth,
        PStatus
    },
    props: {
        id: Number,
        journal: Object,
        value: Boolean
    },
    data() {
        return {
            round: 2,
            dpk: null,
            loading: {
                data: false,
                post: false,
                dpk: false
            },
            selected: {
                JournalEntry: []
            },
            journalEntry: {},
            exchangeRates: [],
            currencies: [],
            baseCurrency: null,
            journalCurrency: null,
            journalEntryDialogProps: {
                id: null,
                display: false
            },
            journalEntryPrintDialogProps: {
                display: false,
                journalEntry: {},
            },  
        }
    },
    computed: {
        totalTransDebit() {
            return getTotal(this.journalEntry.lines, 'trans_debit');
        },
        totalTransCredit() {
            return getTotal(this.journalEntry.lines, 'trans_credit');
        },
        totalBaseDebit() {
            return getTotal(this.journalEntry.lines, 'base_debit');
        },
        totalBaseCredit() {
            return getTotal(this.journalEntry.lines, 'base_credit');
        },
        isForeign() {
            return this.baseCurrency != this.journalCurrency;
        },
        journalEntryHeaders() {
            let headers = [
                    { text: this.$t('account'), divider: false, fixed: true, sortable: false, value: 'account', display: true},
                    { text: this.$t('narration'), divider: false, fixed: true, sortable: false, value: 'narration', display: true},
                    { text: this.$t('debit') + " (" + this.baseCurrency.abbreviation + ") ", divider: false, fixed: true, sortable: false, value: 'base_debit', display: true},
                    { text: this.$t('credit') + " (" + this.baseCurrency.abbreviation + ") ", divider: false, fixed: true, sortable: false, value: 'base_credit', display: true},
                    { text: this.$t('debit') + " (" + this.journalCurrency.abbreviation + ") ", divider: false, fixed: true, sortable: false, value: 'trans_debit', display: this.isForeign},
                    { text: this.$t('credit') + " (" + this.journalCurrency.abbreviation + ") ", divider: false, fixed: true, sortable: false, value: 'trans_credit', display: this.isForeign},
                    { text: this.$t('dimension'), divider: false, fixed: true, sortable: false, value: 'dimension', display: true},
                    { text: "Rate Calculation", divider: false, fixed: true, sortable: false, value: 'rate', display: this.isForeign},
                ]
            return headers.filter(header => header.display == true);
        }
    },
    methods: {
        ...mapActions('accounting/journalEntry', ["fetchJournalEntry"]),
        ...mapActions('accounting/journalVoucher', ["fetchJournalVoucher", "removeJournalVoucher", "reverseJournalVoucher"]),
        ...mapActions('common/snackBar', ["setSnackBars"]),
        formatNumber,
        formatMoney,
        isArabic,
        truncate,
        getColor,
        formatCurrency(amount) {
            return formatMoney(amount, this.journalEntry.currency+' ' , 2);
        },
        findBaseCurrency(currencies, exchangeRates) {
            return this.currencies.find(each => each.id == exchangeRates.base_currency_id);
        },
        setJournalCurrency(currencies, journalEntry) {
            return this.currencies.find(each => each.id == journalEntry.currency_id);
        },
        async fetchDpk() {
            this.loading.dpk = true;
            let resp = await getDpk();
            this.dpk = resp.data.key;
            this.loading.dpk = false;
        },
        /**
         * @todo Cache currencies instead of getting it from server everytime.
         */
        async createdDataFetch(){
            try {
                this.loading.data = true;

                // fetch both journal and currencies.
                let batchOne = [
                    getJournalVoucherById(this.id),
                    getCurrenciesList(),
                ];
                const [journalResp, currenciesResp] = await Promise.all(batchOne);

                // get the journal entry.
                this.journalEntry = journalResp.data;
                this.currencies = currenciesResp.data;
                this.fetchDpk();

                let batchTwo = [
                    searchExchangeRates({date: this.journalEntry.document_date}),
                    getActiveAccountsIn(map(this.journalEntry.lines, "account_id")),
                ];

                // get exchange rates && accounts
                const [exchangeRatesResp, accountsResp] = await Promise.all(batchTwo);

                this.exchangeRates = exchangeRatesResp.data;
                this.accounts = accountsResp.data;

                // set base currency
                this.baseCurrency = this.findBaseCurrency(this.currencies, this.exchangeRates);
                this.journalCurrency = this.setJournalCurrency(this.currencies, this.journalEntry);

                const customRates = extractCustomRateFromJournalData(this.journalEntry, this.accounts);
                this.exchangeRates.rates = populateExchangeRateWithCustomRate(this.exchangeRates.rates, customRates);

                // generate transaction amount
                this.journalEntry.lines.map(line => {
                    const rate = this.exchangeRates.rates.find(rate => rate.currency_id == this.journalEntry.currency_id).customRate;
                    line.trans_debit = line.base_debit / rate;
                    line.trans_credit = line.base_credit / rate;
                    line.currency = this.getAccountCurrencyAbbreviation(line.account_id);
                    line.rateCalculation = formatMoney(Math.abs(line.amount), line.currency, this.round) + " @ " + formatNumber(line.rate, this.round);
                });


                this.journalEntry.baseCurrency = this.baseCurrency.abbreviation;

                this.loading.data = false;
            } catch(error) {
                let message = error.response ? error.response.data.message : error;
                console.log(message);
            }
        },
        getAccountCurrencyAbbreviation(accountId) {
            let abbr =  this.accounts.find(each => each.id == accountId).currency;
            return `${abbr} `
        },
        checkStatus(status) {
            if(status == 'Draft') return true;
            else return false
        },
        openDecisionDialog(params) {
            this.loading.post = true;
            this.$refs.decisionDialog.open(params);
        },
        closeDecisionDialog() {
            this.loading.post = false;
        },
        postToLedger(id) {
            postJournalVoucher(id, this.dpk)
                .then((response) => {
                    this.setSnackBars({ color: "success", message: response.data.message });
                    // this.close();
                    this.createdDataFetch();
                    this.loading.post = false;
                })
                .catch((error) => {
                    this.loading.post= false;
                    let message = error.response ? error.response.data.message : error;
                    this.setSnackBars({ color: "red", message });
                }) 
        },
        openJournalEntryDialog({ editMode, id }) {
            editMode ? this.journalEntryDialogProps.id = id : this.journalEntryDialogProps.id = null;
            this.journalEntryDialogProps.display = true;
        },
        closeJournalEntryDialog(){
            this.journalEntryDialogProps.display = false;
        },
        onDecisionDialogSubmit(params){
            if(params.target == 'post') this.postToLedger(params.id);
            else  if ( params.target == 'reverse') {
                this.reverseJournal(params.id);
            } 
        },
        reverseJournal(id) {
            reverseJournalVoucher(id, this.dpk).then((response) => {
                this.setSnackBars({ color: "success", message: response.data.message });
                this.createdDataFetch();
                // this.close();
            })
            .catch((error) => {
                this.loading.post= false;
                let message = error.response ? error.response.data.message : error;
                this.setSnackBars({ color: "red", message });
            }) 
        //    alert(id);
        },
        onRetryDialogSubmit(params){
            if(params.target == "createdDataFetch") {
                if(params.retry) this.createdDataFetch();
            }
        },
        onDeleteDialogSubmit(params) {
            if(params.target == 'journalEntry') {
                this.removeJournalVoucher(params)
                    .then(() => {
                        this.$refs.deleteSingleDialog.close();
                        this.close();
                        this.$refs.alertDialog.open({type: "success", message: this.$t("records_delete_success")});
                    })
                    .catch((error) => {
                        this.$refs.deleteSingleDialog.close();
                        let message = error.response ? error.response.data.message : error;
                        this.$refs.alertDialog.open({type: "error", message});
                    })
            } 
        },
        openDeleteSingleDialog(params) {
            this.$refs.deleteSingleDialog.open(params);
        },
        openJournalEntryPrintDialog() {
            this.journalEntryPrintDialogProps.journalEntry = this.journalEntry;
            this.journalEntryPrintDialogProps.display = true;
        },
        closeJournalEntryPrintDialog() {
            this.journalEntryPrintDialogProps.display = false;
        },
        close() {
            this.$emit('close');
        }
        
    },
    created () {
        this.createdDataFetch();
    },
}
</script>